import React, { useState } from "react";
import { Select, Checkbox, InputNumber, Alert, Tooltip, Tag, Popconfirm } from "antd";
import { DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { DatePickerItem, InputItem, ButtonItem, fmtFechaDia } from "../../../../Components/Items";
import SelectConceptosNominaItem from "../../../../Components/SelectConceptosNominaItem";
// Qué NO ofrece el selector de conceptos libres. Compartido con la pestaña 2 para que las dos
// escondan exactamente lo mismo.
import { idsNoOfrecibles } from "./conceptosDelRecibo";
import moment from "moment";

const { Option } = Select;

const fmtMXN = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

const thBase = {
  padding: "6px 8px", fontSize: 11, fontWeight: 600, textAlign: "center",
  border: "1px solid #b8a000", whiteSpace: "nowrap", color: "#fff",
};
const TH = { ...thBase, background: "#c9a227" };
const TD = {
  border: "1px solid #e0e0e0", padding: "4px 6px", textAlign: "center",
  fontSize: 12, verticalAlign: "middle",
};
const TDY = { ...TD, background: "#fffde7" };

const Campo = ({ etiqueta, children, ancho }) => (
  <div style={{ minWidth: ancho || 130 }}>
    <label style={{ display: "block", fontSize: 11, color: "#888", marginBottom: 3 }}>
      {etiqueta}
    </label>
    {children}
  </div>
);

const Dato = ({ etiqueta, valor }) => (
  <Campo etiqueta={etiqueta}>
    <div
      style={{
        fontSize: 12, fontWeight: 600, padding: "4px 8px",
        background: "#fafafa", border: "1px solid #e0e0e0", borderRadius: 2,
      }}
    >
      {valor}
    </div>
  </Campo>
);

// Era `moment.utc(f)`, que corre un día toda fecha capturada con el TECLADO después de las 18:00
// (DatePickerItem le estampa la hora actual). Con esto la fecha de alta se lee igual aquí y en la
// ficha del empleado, sin importar por qué ruta se guardó. Ver Components/fmtFechaDia.js.
// Seguro para los otros dos usos de abajo (fechas de Periodo y fechaRegistro de la póliza): ésas
// están ancladas al mediodía y la regla las lee igual que antes.
const fmtDia = (f) => fmtFechaDia(f);

/**
 * Pestaña 1 — la configuración del cálculo. Replica el bloque "Configuración del cálculo de
 * finiquito" de CONTPAQi: bases de sólo lectura arriba, el selector del periodo de aplicación,
 * la tabla de conceptos con Aplica + Días editables, y los criterios fiscales.
 */
const ConfiguracionFiniquito = ({
  empleado, causas, fechaBaja, causaFiniquito, motivoBaja, aniosCompletosIsr,
  propuesta, filas, conceptos, periodos, periodoAplicacionId, bloqueado,
  // `bloqueado` congela los CONCEPTOS (días, conceptos libres, años de ISR) y sólo se prende
  // cuando el recibo ya está timbrado. `periodoBloqueado` congela el PERIODO DE APLICACIÓN desde
  // el momento de aplicar: cambiarlo sería mover la línea a otra prenómina, que es deshacer y
  // volver a aplicar, no una edición.
  periodoBloqueado,
  // Fecha de pago de la liquidación: la de la PÓLIZA y la del @FechaPago del CFDI. Su candado no
  // es el estatus sino que la póliza ya exista — la contabilización es append-only.
  fechaPago, fechaPagoBloqueada, fechaRegistroPoliza, numeroPoliza, advertenciasFechaPago,
  motivoPeriodos, cargandoPeriodos, agregandoALaPrenomina, fechaBajaBloqueada,
  onCambio, onCambiarConcepto, onCambiarPeriodo, onAgregarAPrenomina, onSueldoCapturado,
  // Conceptos libres: `extras` es lo capturado y `filasExtra` lo que devolvió el motor para
  // cada uno. `conceptosLinea` e `isrFiniquitoConceptoId` sólo se usan para no ofrecer en el
  // selector algo que ya se ve en el recibo —eso se edita en la pestaña de percepciones y
  // deducciones— o que el backend va a rechazar.
  extras, filasExtra, conceptosLinea, isrFiniquitoConceptoId, empresaId, headersToken,
  onAgregarExtra, onCambiarExtra, onQuitarExtra,
}) => {
  // Captura del renglón de alta. Vive aquí y no en la pantalla porque es un borrador: no vale
  // nada hasta que se presiona el +, y subirlo obligaría a la pantalla a recalcular por cada tecla.
  const [nuevo, setNuevo] = useState({ tipo: 1, conceptoNominaId: null, descripcion: "", importe: null });

  // El periodo marcado, para saber si hay que ofrecer agregar al empleado a su prenómina.
  const periodoElegido = (periodos || []).find((p) => p._id === periodoAplicacionId);
  if (!empleado) return null;

  const calculadoExtra = new Map(
    (filasExtra || []).map((f) => [String(f.conceptoNominaId), f])
  );

  // Lo que el selector NO debe ofrecer: los nueve conceptos del finiquito y el ISR de finiquito
  // (el backend los rechaza), los que ya se agregaron, y los renglones de la línea que YA SE VEN
  // en el recibo —a ésos se les edita el importe en la pestaña 2—. Un concepto de la línea que
  // quedó en CERO sí se ofrece: está oculto en esa pestaña, así que excluirlo aquí lo dejaba
  // inalcanzable por los dos lados. Ver conceptosDelRecibo.js.
  const yaEnElRecibo = idsNoOfrecibles({
    filas, conceptosLinea, extras, isrFiniquitoConceptoId,
  });

  const confirmarAgregar = () => {
    if (!nuevo.conceptoNominaId || nuevo.importe === null || nuevo.importe === undefined) return;
    onAgregarExtra({
      conceptoNominaId: nuevo.conceptoNominaId,
      descripcion: nuevo.descripcion,
      tipo: nuevo.tipo,
      importe: Number(nuevo.importe),
    });
    setNuevo({ tipo: nuevo.tipo, conceptoNominaId: null, descripcion: "", importe: null });
  };

  return (
    <>
      {/* ── Cabecera del empleado ─────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14 }}>
        <Dato etiqueta="Empleado" valor={`${empleado.numero ?? ""} — ${empleado.nombre}`} />
        <Dato etiqueta="Fecha de alta" valor={fmtDia(empleado.fechaAlta)} />
        <Dato etiqueta="Sueldo diario" valor={fmtMXN(empleado.salarioDiario)} />
      </div>

      {/* ── Datos de la baja ──────────────────────────────────────────────── */}
      <div
        style={{
          background: "#f0f5ff", border: "1px solid #d6e4ff", borderRadius: 4,
          padding: 12, marginBottom: 14,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
          Configuración del cálculo de finiquito
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
          {/* Los tres se congelan juntos en cuanto la baja está escrita: son el registro de un
              hecho, y con esa fecha ya se recalcularon las prenóminas afectadas. Se usa
              `fechaBajaBloqueada` y no `bloqueado` porque con una baja sin finiquito los
              conceptos de abajo SÍ siguen siendo editables. */}
          <Campo etiqueta="Fecha de baja">
            {/* Los pickers de esta versión de antd usan moment, no dayjs */}
            <DatePickerItem
              value={fechaBaja}
              disabled={fechaBajaBloqueada}
              onChange={(d) => onCambio({ fechaBaja: d || moment() })}
            />
          </Campo>
          <Campo etiqueta="Causa del finiquito" ancho={210}>
            <Select
              value={causaFiniquito}
              disabled={fechaBajaBloqueada}
              onChange={(v) => onCambio({ causaFiniquito: v })}
              style={{ width: 210 }}
            >
              {(causas || []).map((c) => (
                <Option key={c.value} value={c.value}>{c.label}</Option>
              ))}
            </Select>
          </Campo>
          <Campo etiqueta="Motivo (opcional)" ancho={220}>
            <InputItem
              value={motivoBaja}
              disabled={fechaBajaBloqueada}
              onChange={(e) => onCambio({ motivoBaja: e.target.value })}
              placeholder="Detalle del motivo"
            />
          </Campo>
        </div>

        {/* ── Periodo de aplicación ───────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end", marginTop: 12 }}>
          <Campo etiqueta="Periodo de aplicación" ancho={330}>
            <Select
              value={periodoAplicacionId}
              disabled={periodoBloqueado || !periodos?.length}
              loading={cargandoPeriodos}
              onChange={onCambiarPeriodo}
              style={{ width: 330 }}
              placeholder={
                cargandoPeriodos ? "Buscando periodos…" : "Captura la fecha de baja"
              }
            >
              {/* Un periodo con `puedeAgregarse` se deja SELECCIONABLE aunque no esté
                  disponible: el botón de rescate de abajo sólo aparece para el periodo
                  marcado, así que si éstos quedaran deshabilitados el remedio sería
                  inalcanzable — se veía el motivo pero no había forma de actuar sobre él.
                  Calcular sigue apagado hasta que la línea exista (ver `puedeCalcular`). */}
              {(periodos || []).map((p) => (
                <Option
                  key={p._id}
                  value={p._id}
                  disabled={!p.disponible && !p.puedeAgregarse}
                >
                  {`${p.serie || ""}-${p.numero} · ${fmtDia(p.fechaInicio)} al ${fmtDia(p.fechaFin)}`}
                  {p.esPeriodoDeLaBaja ? " (periodo de la baja)" : ""}
                  {p.esPeriodoActual ? " (periodo actual)" : ""}
                  {p.sinPrenomina
                    ? (p.seCrearaLinea ? " — se creará la prenómina y su línea" : " — se creará la prenómina")
                    : ""}
                  {p.disponible
                    ? ""
                    : p.puedeAgregarse
                      ? " — falta agregar al empleado (elígelo y usa el botón)"
                      : ` — ${p.motivoNoDisponible}`}
                </Option>
              ))}
            </Select>
          </Campo>

          {/* Va en esta fila y no en la de la baja: aquélla se congela entera al aplicar porque
              son el registro de un hecho ya escrito, y ésta sigue viva hasta que se genere la
              póliza. Además es lo mismo que el periodo — dónde y cuándo se paga, no cuándo
              terminó la relación laboral. */}
          <Campo etiqueta="Fecha de pago" ancho={170}>
            <Tooltip
              title={
                "Fecha en que se le paga la liquidación. Es la que lleva la PÓLIZA contable de este "
                + "finiquito y el campo Fecha de pago de su CFDI. Si la dejas vacía se usa la fecha "
                + "de pago de la prenómina, como hasta ahora. Ojo: es distinta de la fecha de pago "
                + "de la prenómina, que sigue siendo la del resto de los empleados."
              }
            >
              <div>
                <DatePickerItem
                  value={fechaPago}
                  disabled={fechaPagoBloqueada}
                  allowClear
                  onChange={(d) => onCambio({ fechaPago: d || null })}
                />
              </div>
            </Tooltip>
          </Campo>

          {/* El periodo existe y su prenómina está abierta, pero al empleado le falta la
              línea. Se corrige aquí en vez de mandar al usuario a la pantalla de Prenómina. */}
          {!periodoBloqueado && periodoElegido?.puedeAgregarse && (
            <ButtonItem
              type="primary"
              loading={agregandoALaPrenomina}
              onClick={() => onAgregarAPrenomina(periodoElegido)}
            >
              Agregar a la prenómina
            </ButtonItem>
          )}
          {propuesta && (
            <>
              <Dato etiqueta="Antigüedad" valor={`${(propuesta.antiguedadAnios || 0).toFixed(3)} años`} />
              {/* El endpoint manda `diasTrabajadosAnio`; antes se leía `diasTranscurridosAnio`
                  (el nombre que usa el snapshot Finiquito) y por eso salía siempre en 0. */}
              <Dato etiqueta="Días trans. en el año" valor={propuesta.diasTrabajadosAnio ?? 0} />
              <Dato etiqueta="S.M. zona" valor={fmtMXN(propuesta.salarioMinimoZona)} />
              <Dato etiqueta="Salario base finiquito" valor={fmtMXN(propuesta.salarioBaseFiniquito)} />
              {/* Los divisores configurados: es lo primero que hay que revisar cuando los días
                  no cuadran contra CONTPAQi, y no se ven en ningún otro lado de la pantalla. */}
              <Dato
                etiqueta="Días del año (aguin. / vac.)"
                valor={`${propuesta.diasAnioAguinaldo ?? 365} / ${propuesta.diasAnioVacaciones ?? 365}`}
              />
            </>
          )}
        </div>
        {motivoPeriodos && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 10 }}
            message={motivoPeriodos}
          />
        )}

        {/* Ya se contabilizó: la fecha deja de ser una promesa y pasa a ser un hecho. */}
        {fechaPagoBloqueada && fechaRegistroPoliza && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 10 }}
            message={`La póliza contable de este finiquito ya se generó con fecha ${fmtDia(fechaRegistroPoliza)}`
              + (numeroPoliza != null ? ` (póliza ${numeroPoliza})` : "")}
            description={
              "La fecha de pago ya no se puede cambiar desde aquí: la contabilización es "
              + "acumulativa, y volver a timbrar —incluso después de cancelar el CFDI— reusa esa "
              + "misma póliza y conserva su fecha. Si hay que moverla, se corrige desde Pólizas."
            }
          />
        )}

        {!fechaPagoBloqueada && advertenciasFechaPago?.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 10 }}
            message="Revisa la fecha de pago"
            description={
              <div>
                {advertenciasFechaPago.map((a, i) => (
                  <p key={i} style={{ marginBottom: i === advertenciasFechaPago.length - 1 ? 0 : 4 }}>
                    {a}
                  </p>
                ))}
              </div>
            }
          />
        )}

        <div style={{ fontSize: 11, color: "#666", marginTop: 8 }}>
          Se puede aplicar en el periodo de la baja y en los dos siguientes. Si el empleado ya
          venía dado de baja, se agregan además el periodo actual y los dos que le siguen — es
          el finiquito que se paga tarde. Sólo aparecen habilitados los que tienen la prenómina
          abierta <b>y</b> al empleado dentro de ella. En un periodo posterior al de la baja el
          sueldo y el séptimo día salen en cero: ya no laboró ningún día de ese periodo. Si aun así
          hay que pagarlos, hay que marcar <b>Aplica</b> en el renglón de Sueldo y capturar los
          días — con el concepto apagado el importe se queda en cero aunque se capturen.
        </div>
      </div>

      {/* ── Tabla de conceptos ────────────────────────────────────────────── */}
      {!filas ? (
        <Alert
          type="info"
          showIcon
          message="Captura la fecha de baja y la causa, y presiona Calcular para ver la propuesta de días."
          // Los conceptos agregados sobreviven a un cambio de fecha, causa o periodo (no dependen
          // de ninguno de los tres), pero la tabla donde se ven sí desaparece con la
          // previsualización. Se dice aquí para que no queden invisibles afectando el cálculo.
          description={
            (extras || []).length > 0
              ? `Se conservan ${extras.length} concepto(s) que agregaste: vuelven a aparecer al calcular.`
              : undefined
          }
        />
      ) : (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            Definición de conceptos en el finiquito
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr>
                  <th style={TH}>Descripción</th>
                  <th style={TH}>Aplica</th>
                  <th style={TH}>Días propuestos</th>
                  <th style={TH}>Días a pagar</th>
                  <th style={TH}>Importe</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => {
                  const cap = conceptos[f.key] || {};
                  const aplica = cap.aplica ?? f.aplica;
                  const dias = cap.dias ?? f.diasCapturados;
                  const modificado = Math.abs((dias || 0) - (f.diasPropuestos || 0)) > 0.0005;

                  return (
                    <tr key={f.key}>
                      <td style={{ ...TD, textAlign: "left" }}>
                        {f.label}
                        {!f.inyecta && (
                          <Tooltip
                            title={
                              "El sueldo y el séptimo día los paga la fórmula ordinaria de la prenómina, " +
                              "con los días que corresponden hasta la fecha de baja. Si cambias los días, " +
                              "el importe deja de calcularse con esa fórmula y se fija en días × salario " +
                              "diario. Ojo: los días cotizables del IMSS NO se mueven — se pagarían los " +
                              "días que captures, pero se cotizarían los realmente trabajados."
                            }
                          >
                            <span style={{ color: "#999", fontSize: 11, marginLeft: 6 }}>
                              (de la nómina ordinaria)
                            </span>
                          </Tooltip>
                        )}
                        {f.key === "septimoDia" && (
                          <Tooltip title="El séptimo día no tiene interruptor propio: se apaga y se prende con el sueldo. Sus días, en cambio, son independientes y se capturan aparte.">
                            <span style={{ color: "#999", fontSize: 11, marginLeft: 6 }}>
                              — se apaga con el sueldo
                            </span>
                          </Tooltip>
                        )}
                        {f.sinCuentas && (
                          <Tooltip title="Este concepto no tiene cuentas contables asignadas: al timbrar se omitirá de la póliza. Se asignan en el asistente de configuración de nómina.">
                            <span style={{ color: "#C9A227", fontSize: 11, marginLeft: 6 }}>
                              ⚠ sin cuentas
                            </span>
                          </Tooltip>
                        )}
                      </td>
                      <td style={TD}>
                        <Checkbox
                          checked={aplica}
                          disabled={bloqueado || !f.desactivable}
                          onChange={(e) => onCambiarConcepto(f.key, { aplica: e.target.checked })}
                        />
                      </td>
                      <td style={TD}>{(f.diasPropuestos || 0).toFixed(4)}</td>
                      <td style={f.editable ? TDY : TD}>
                        {f.editable ? (
                          <InputNumber
                            value={dias}
                            min={0}
                            step={0.0001}
                            // 4 decimales y no 3: los proporcionales se calculan con precisión
                            // completa y truncarlos aquí movería el importe unos centavos.
                            precision={4}
                            disabled={bloqueado || !aplica}
                            onChange={(v) => onCambiarConcepto(f.key, { dias: v ?? 0 })}
                            // Al salir del campo de Sueldo se ofrece ajustar el séptimo día
                            // proporcional. Va en onBlur y no en onChange porque el modal
                            // saltaría en cuanto se escribiera el primer dígito.
                            onBlur={
                              f.key === "sueldo" && onSueldoCapturado
                                ? () => onSueldoCapturado()
                                : undefined
                            }
                            style={{
                              width: 110,
                              ...(modificado ? { borderColor: "#1677ff", fontWeight: 600 } : {}),
                            }}
                          />
                        ) : (
                          (dias || 0).toFixed(4)
                        )}
                      </td>
                      <td style={{ ...TD, textAlign: "right" }}>
                        {/* En azul, igual que en el sobre-recibo, cuando el importe lo puso una
                            persona y no la fórmula: puede venir de los días capturados aquí
                            (sueldo y séptimo) o del importe editado en el desglose. */}
                        {f.importeManual !== null && f.importeManual !== undefined ? (
                          <Tooltip title="Importe capturado: la fórmula de este concepto no se usó. Sale de los días que capturaste o del importe que editaste en Percepciones y Deducciones, y desde allá se quita con la marca M ✕.">
                            <span style={{ color: "#1677ff", fontWeight: 600 }}>
                              {fmtMXN(f.importe)}
                            </span>
                          </Tooltip>
                        ) : (
                          fmtMXN(f.importe)
                        )}
                      </td>
                    </tr>
                  );
                })}

                {/* ── Conceptos libres ──────────────────────────────────────── */}
                {/* No tienen días: su importe es capturado y REEMPLAZA la fórmula del concepto,
                    que es lo que hace que funcionen con cualquiera del catálogo. */}
                {(extras || []).map((e) => {
                  const calc = calculadoExtra.get(String(e.conceptoNominaId));
                  const capturado = Number(e.importe) || 0;
                  // Normalmente el recibo queda en el importe capturado. Cuando no, es porque el
                  // concepto tiene fórmulas propias (un tope, o es informativo `noSuma`), y ver
                  // los dos números es lo único que lo explica.
                  const difiere = calc && Math.abs((calc.importe || 0) - capturado) > 0.005;

                  return (
                    <tr key={e.conceptoNominaId}>
                      <td style={{ ...TD, textAlign: "left" }}>
                        {e.descripcion}
                        <Tag color="blue" style={{ marginLeft: 6, fontSize: 10, padding: "0 4px" }}>
                          {e.tipo === 2 ? "deducción agregada" : "percepción agregada"}
                        </Tag>
                        {calc?.sinCuentas && (
                          <Tooltip title="Este concepto no tiene cuentas contables asignadas: al timbrar se omitirá de la póliza. Se asignan en el asistente de configuración de nómina.">
                            <span style={{ color: "#C9A227", fontSize: 11, marginLeft: 6 }}>
                              ⚠ sin cuentas
                            </span>
                          </Tooltip>
                        )}
                        {!bloqueado && (
                          <Popconfirm
                            title="¿Quitar este concepto del finiquito?"
                            okText="Quitar"
                            cancelText="Cancelar"
                            onConfirm={() => onQuitarExtra(e.conceptoNominaId)}
                          >
                            <DeleteOutlined
                              style={{ marginLeft: 8, color: "#ff4d4f", cursor: "pointer" }}
                            />
                          </Popconfirm>
                        )}
                        {difiere && (
                          <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>
                            En el recibo quedó en {fmtMXN(calc.importe)} — el concepto aplica sus
                            propias fórmulas de tope.
                          </div>
                        )}
                        {calc?.noSuma && (
                          <div style={{ fontSize: 10, color: "#C9A227", marginTop: 2 }}>
                            Este concepto es informativo: se imprime en el recibo pero <b>no suma
                            al neto</b>.
                          </div>
                        )}
                      </td>
                      <td style={TD}>
                        <Checkbox
                          checked={e.aplica !== false}
                          disabled={bloqueado}
                          onChange={(ev) =>
                            onCambiarExtra(e.conceptoNominaId, { aplica: ev.target.checked })
                          }
                        />
                      </td>
                      <td style={TD}>—</td>
                      <td style={TD}>—</td>
                      <td style={{ ...TDY, textAlign: "right" }}>
                        <InputNumber
                          value={e.importe}
                          min={0}
                          precision={2}
                          disabled={bloqueado || e.aplica === false}
                          onChange={(v) => onCambiarExtra(e.conceptoNominaId, { importe: v ?? 0 })}
                          style={{ width: 120 }}
                        />
                      </td>
                    </tr>
                  );
                })}

                {/* ── Alta de un concepto libre ─────────────────────────────── */}
                {!bloqueado && (
                  <tr style={{ background: "#fafafa" }}>
                    <td style={{ ...TD, textAlign: "left" }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <Select
                          size="small"
                          value={nuevo.tipo}
                          onChange={(v) =>
                            setNuevo({ ...nuevo, tipo: v, conceptoNominaId: null, descripcion: "" })
                          }
                          style={{ width: 118 }}
                        >
                          <Option value={1}>Percepción</Option>
                          <Option value={2}>Deducción</Option>
                        </Select>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <SelectConceptosNominaItem
                            tipo={nuevo.tipo}
                            empresaId={empresaId}
                            headersToken={headersToken}
                            // Lo que ya está en el recibo no se agrega: se le edita el importe en
                            // la pestaña de percepciones y deducciones.
                            excludeIds={yaEnElRecibo}
                            value={nuevo.conceptoNominaId}
                            onChange={(id, concepto) =>
                              setNuevo({
                                ...nuevo,
                                conceptoNominaId: id,
                                descripcion: concepto?.descripcion || "",
                              })
                            }
                            placeholder={
                              nuevo.tipo === 2 ? "+ Agregar deducción…" : "+ Agregar percepción…"
                            }
                            width="100%"
                          />
                        </div>
                      </div>
                    </td>
                    <td style={TD} />
                    <td style={TD} />
                    <td style={TD} />
                    <td style={{ ...TD, textAlign: "right" }}>
                      <div
                        style={{
                          display: "flex", gap: 4, alignItems: "center", justifyContent: "flex-end",
                        }}
                      >
                        <InputNumber
                          size="small"
                          style={{ width: 100 }}
                          min={0}
                          precision={2}
                          placeholder="$0.00"
                          value={nuevo.importe}
                          onChange={(v) => setNuevo({ ...nuevo, importe: v })}
                          onPressEnter={confirmarAgregar}
                        />
                        <Tooltip title="Agregar el concepto al finiquito">
                          <PlusCircleOutlined
                            style={{ color: "#1677ff", cursor: "pointer", fontSize: 16 }}
                            onClick={confirmarAgregar}
                          />
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: "#666", marginTop: 6 }}>
            Al cambiar días o importes hay que presionar <b>Recalcular</b> para que se actualicen
            los importes, el gravado/exento y el ISR. Agregar, quitar o desactivar un concepto
            agregado sí recalcula solo.
            <br />
            En los conceptos <b>agregados</b> el importe que capturas <b>reemplaza la fórmula</b> del
            concepto, así que sirve para cualquiera del catálogo; el gravado y el exento sí salen de
            sus propias fórmulas. Para cambiar el importe de un concepto que ya está en el recibo
            —el sueldo, el aguinaldo, el ISR— edítalo en <b>Percepciones y Deducciones</b>.
          </div>

          {/* ── Criterios fiscales ───────────────────────────────────────── */}
          <div
            style={{
              marginTop: 14, padding: 12, background: "#fafafa",
              border: "1px solid #e0e0e0", borderRadius: 4,
              display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-end",
            }}
          >
            <Campo etiqueta="Años completos ISR" ancho={140}>
              <Tooltip
                title={
                  "Multiplica el tope de exención de 90 UMA por año (Art. 93 fracc. XIII LISR) y " +
                  "es el NumAñosServicio del CFDI. Se propone el redondeo de la antigüedad: " +
                  "año completo el que excede de 6 meses."
                }
              >
                <InputNumber
                  value={aniosCompletosIsr ?? 0}
                  min={0}
                  step={1}
                  precision={0}
                  disabled={bloqueado}
                  onChange={(v) => onCambio({ aniosCompletosIsr: v ?? 0 })}
                  style={{ width: 140 }}
                />
              </Tooltip>
            </Campo>
            <div style={{ fontSize: 11, color: "#666", maxWidth: 460 }}>
              La exención de los pagos por separación es una <b>bolsa compartida</b> entre
              indemnización, separación única y prima de antigüedad: cada concepto exenta sólo el
              remanente que dejaron los anteriores. El detalle renglón por renglón sale en la
              Hoja de Trabajo.
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ConfiguracionFiniquito;
